import * as offerAssetApi from './api';
import hierarchyApi from 'features/asset/hierarchy/api';
import * as hierarchyActions from 'features/asset/hierarchy/actionCreators';
import * as actionTypes from './actionTypes';
import * as offerAssetEventTypes from 'features/offer/edit/assets/asset/eventTypes';
import * as hierarchyActionTypes from 'features/asset/hierarchy/actionTypes';
import * as eventTypes from './eventTypes';
import i18next from 'i18next';
import { sendHttpRequest, sendHttpCommandAndWaitForEvent } from 'actions/actionCreators/httpActions';
import { showError, showWarning, showInfo } from 'actions/actionCreators/snackbarActions';
import { loadCurrencies } from 'actions/actionCreators/dictionariesActions';
import { createOffer } from 'actions/actionCreators/offer/offerActions';
import { collection } from 'utils/immutable';
import { getAssetHierarchyId, getIdenticalAssetConditions } from 'features/asset/hierarchy/selectors';
import { hierarchyIsValid, applyHierarchyValidationRules } from 'features/asset/hierarchy/rules';
import offerTypes from 'constants/offerType';
import documentTypeIds from 'constants/documentTypeIds';
import { emptyString } from 'constants/emptyConst';
import { camelize, camelizeKeys } from 'humps';
import { cloneDeep } from 'lodash';
import { format } from 'date-fns';
import { ControlType } from '@totalsoft/rocket-ui';
import { getDynamicFieldControlType } from 'utils/functions';
import { supplierType } from './constants';

export function initAssetData(documentId, siteId, documentDetailId, isFacilityFrame, isAlphaFunctionality = false) {
  return async (dispatch, getState) => {
    const { initializing } = getState().currentOffer.assetData;
    if (initializing) return;

    dispatch({ type: actionTypes.INIT_ASSET_DATA_STARTED });

    let asset = null;
    if (documentDetailId) {
      await dispatch(loadOfferAsset(documentId, siteId, documentDetailId));
      const loadedAsset = getState().currentOffer.assetData.assets.find(x => x.documentDetailId === documentDetailId);
      if (isFacilityFrame) {
        const emptyAssetHierarchy = await dispatch(hierarchyActions.retrieveAssetHierarchyLevels(loadedAsset.assetTypeId));
        asset = {
          ...loadedAsset,
          hierarchy: mergeHierarchyLevels(loadedAsset.hierarchy, emptyAssetHierarchy)
        };
      } else {
        asset = loadedAsset;
      }
    } else {
      await dispatch(loadAssetDefaults(isAlphaFunctionality));
      asset = { ...getState().assetData.defaultAsset, documentId, siteId };
    }

    await dispatch(hierarchyActions.loadAssetTypes());
    await dispatch(loadCurrencies());

    const assetInEditPromise = dispatch({ type: actionTypes.SET_ASSET_IN_EDIT, payload: { asset } });
    const assetHierarchyTriggersPromise = dispatch(hierarchyActions.checkAssetHierarchyTriggers(asset.hierarchy));

    Promise.all([assetInEditPromise, assetHierarchyTriggersPromise]).then(() => {
      dispatch({ type: actionTypes.INIT_ASSET_DATA_COMPLETED });
    });
  };
}

export function setAssetInEdit(assetInfo, editRight) {
  return async (dispatch, getState) => {
    let asset = getState().currentOffer.assetData.inEdit.asset.document;
    /* Get the current and new hierarchy id values that have the property trigger */
    const currentHierarchyId = asset.hierarchy.find(x => x.triggers.properties && x.id)?.id;
    const newHierarchyId = assetInfo.hierarchy.find(x => x.triggers.properties && x.id)?.id;

    dispatch({ type: actionTypes.SET_ASSET_IN_EDIT, payload: { asset: { ...asset, ...assetInfo } } });

    /* Reload properties if new hierarchy is set */
    if (newHierarchyId && currentHierarchyId !== newHierarchyId) {
      /* Load assetHierarchyProperties for new id */
      await dispatch(hierarchyActions.loadAssetHierarchyProperties(newHierarchyId));
      /* Retrieve loaded assetHierarchyProperties */
      const assetHierarchyProperties = getState().assetData.assetHierarchyProperties[newHierarchyId];
      /* Assign new assetHierarchyProperties to current asset in edit */
      let properties = getState().currentOffer.assetData.inEdit.asset.document.properties;
      properties = addOrUpdateAssetProperties(assetHierarchyProperties, properties);
      await dispatch(changeAssetProperty('properties', properties, editRight));
    }
  };
}

export function setAssetDirty() {
  return async dispatch => {
    dispatch({ type: actionTypes.SET_ASSET_DIRTY });
  };
}

export function deleteOfferAsset(asset) {
  return async dispatch => {
    try {
      dispatch({ type: actionTypes.DELETE_OFFER_ASSET_STARTED });
      await dispatch(
        sendHttpCommandAndWaitForEvent(
          offerAssetApi.deleteOfferAsset(asset.documentDetailId, asset.documentId, asset.siteId),
          'RunningOfferCommand',
          [eventTypes.OFFER_ASSET_DELETED]
        )
      );
      dispatch({ type: actionTypes.DELETE_OFFER_ASSET_SUCCEEDED });
    } catch (error) {
      dispatch({ type: actionTypes.DELETE_OFFER_ASSET_FAILED });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function duplicateAsset(callbackFn = () => {}, isFacilityFrame, editRight) {
  return async (dispatch, getState) => {
    const { viewType } = getState().params.items;
    const { isSecondHand, itemId, assetTypeId, category, classification } = getState().currentOffer.assetData.inEdit.asset.document;
    const mandatoryPropertiesConfigured = await dispatch(
      getMandatoryPropertiesConfigured(itemId, assetTypeId, category, classification, isFacilityFrame)
    );

    dispatch({
      type: actionTypes.VALIDATE_ASSET,
      payload: { financedAssetDealerWrite: editRight, isSecondHand, isFacilityFrame, mandatoryPropertiesConfigured, viewType }
    });

    const asset = getState().currentOffer.assetData.inEdit.asset;
    if (!validateAsset(asset, isFacilityFrame, getState, dispatch)) {
      return;
    }

    const requestTypeId = getRequestType(isFacilityFrame, getState);

    dispatch({ type: actionTypes.DUPLICATE_OFFER_ASSET_STARTED });
    const hasWorkingHours = getState().currentOffer.assetData.inEdit.secondHandProperties[asset.document.itemId]?.hasWorkingHours;
    const defaultValue = getState().currentOffer.assetData.inEdit.secondHandProperties[asset.document.itemId]?.defaultValue;

    let newAsset = createRequestBody({
      asset: asset.document,
      isFacilityFrame,
      requestTypeId,
      hasWorkingHours,
      defaultValue
    });
    newAsset.documentDetailId = null;

    let event = null;
    try {
      event = await dispatch(createOfferAsset(newAsset));

      if (event.ok) {
        const { documentId, siteId } = event.eventBody;
        await dispatch({
          type: actionTypes.DUPLICATE_OFFER_ASSET_SUCCEEDED,
          payload: { documentDetailId: newAsset.documentDetailId }
        });
        callbackFn(documentId, siteId);
      } else {
        dispatch({ type: actionTypes.DUPLICATE_OFFER_ASSET_FAILED });
      }
    } catch (error) {
      dispatch({ type: actionTypes.DUPLICATE_OFFER_ASSET_FAILED });
    }
  };
}

export function loadOfferAssets(documentId, siteId) {
  return async (dispatch, getState) => {
    if ((!documentId && documentId !== 0) || (!siteId && siteId !== 0)) {
      return;
    }

    if (getState().currentOffer.assetData.assets.isLoaded) {
      return;
    }

    dispatch({ type: actionTypes.LOAD_OFFER_ASSETS_STARTED, payload: { documentId, siteId } });
    try {
      let assets = await dispatch(sendHttpRequest(offerAssetApi.getOfferAssets(documentId, siteId)));
      dispatch({ type: actionTypes.LOAD_OFFER_ASSETS_SUCCEEDED, payload: { assets } });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_OFFER_ASSETS_FAILED, payload: { documentId, siteId, error } });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadOfferAssetsHeader(documentId, siteId) {
  return async (dispatch, getState) => {
    if ((!documentId && documentId !== 0) || (!siteId && siteId !== 0)) {
      return;
    }

    if (getState().currentOffer.assetData.assetsHeader.isLoaded) {
      return;
    }

    dispatch({ type: actionTypes.LOAD_OFFER_ASSETS_HEADER_STARTED, payload: { documentId, siteId } });
    try {
      let assets = await dispatch(sendHttpRequest(offerAssetApi.getOfferAssetsHeader(documentId, siteId)));
      dispatch({ type: actionTypes.LOAD_OFFER_ASSETS_HEADER_SUCCEEDED, payload: { assets } });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_OFFER_ASSETS_HEADER_FAILED, payload: { documentId, siteId, error } });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

function loadOfferAsset(documentId, siteId, documentDetailId) {
  return async (dispatch, getState) => {
    const assets = getState().currentOffer.assetData.assets;
    let asset = assets.find(a => a.documentDetailId === documentDetailId);
    if (!asset) {
      asset = await dispatch(sendHttpRequest(offerAssetApi.getOfferAsset(documentId, siteId, documentDetailId)));
      dispatch({ type: actionTypes.LOAD_OFFER_ASSET_SUCCEEDED, payload: { asset } });
    }
  };
}

function loadAssetDefaults(isAlphaFunctionality = false) {
  return async (dispatch, getState) => {
    try {
      let defaultAsset = getState().assetData.defaultAsset;
      if (defaultAsset.isLoaded) {
        let assetPropertiesByTypeId = getState().assetData.properties.byTypeId[defaultAsset.assetTypeId];
        if (assetPropertiesByTypeId) return;
      }
      if (isAlphaFunctionality)
        !defaultAsset.isLoaded && (defaultAsset = await dispatch(sendHttpRequest(hierarchyApi.getAssetDefaultsAlpha())));
      else !defaultAsset.isLoaded && (defaultAsset = await dispatch(sendHttpRequest(hierarchyApi.getAssetDefaults())));

      let payload = { defaultAsset };
      if (defaultAsset.itemId) {
        await dispatch(loadAssetProperties(defaultAsset.itemId));

        const { properties, firstOwner, remat } = getState().assetData.properties.byTypeId[defaultAsset.itemId];
        payload = { defaultAsset: { ...defaultAsset, properties, firstOwner, remat } };
      }

      dispatch({ type: hierarchyActionTypes.LOAD_ASSET_DEFAULTS_SUCCEEDED, payload });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadAssetProperties(assetTypeId) {
  return async (dispatch, getState) => {
    const properties = getState().assetData.properties?.byTypeId[assetTypeId];
    if (properties && (properties.loading || properties.loaded)) {
      return;
    }
    dispatch({ type: actionTypes.LOAD_ASSET_PROPERTIES_STARTED, payload: { assetTypeId } });
    try {
      const result = await dispatch(sendHttpRequest(offerAssetApi.getAssetTypeProperties(assetTypeId)));

      const properties = {
        ...result,
        properties: result.properties.map(prop => {
          prop.value = prop.defaultValue;
          return prop;
        })
      };

      dispatch({ type: actionTypes.LOAD_ASSET_PROPERTIES_SUCCEEDED, payload: { assetTypeId, properties } });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadMandatoryPropertiesConfigured(assetId, assetTypeId, assetCategory, assetClassification) {
  return async (dispatch, getState) => {
    if (!assetId) return;

    const key = `${assetId}_${assetTypeId}_${assetCategory ?? ''}_${assetClassification ?? ''}`;
    const properties = getState().assetData.mandatoryPropertiesConfigured[key];
    if (properties?.loading || properties?.loaded) return;

    dispatch({ type: actionTypes.LOAD_MANDATORY_PROPERTIES_CONFIGURED_STARTED, payload: { key } });
    await dispatch(
      sendHttpRequest(offerAssetApi.getAssetMandatoryProperties(assetId, assetTypeId, assetCategory, assetClassification))
    ).then(properties => dispatch({ type: actionTypes.LOAD_MANDATORY_PROPERTIES_CONFIGURED_SUCCEEDED, payload: { key, properties } }));
  };
}

const replacePropertyValues = (defaultProperties, properties) => {
  if (!defaultProperties) return [];
  if (properties == null)
    return defaultProperties.map(item => {
      const { defaultValue, ...rest } = item;
      return {
        ...rest,
        value: defaultValue
      };
    });

  return defaultProperties.map(({ defaultValue, ...rest }) => {
    const replacement = properties.filter(x => x.propertyId === rest.propertyId)[0];

    if (replacement) {
      return {
        ...rest,
        value: replacement.propertyValue
      };
    }

    return {
      ...rest,
      value: defaultValue
    };
  });
};

export function loadAssetData(chassisNumber, commandNumber) {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: actionTypes.LOAD_ASSET_DATA_STARTED });

      let requestType = chassisNumber
        ? i18next.t('OfferEdit.Asset.Info.ChassisNumber')
        : commandNumber
        ? i18next.t('OfferEdit.Asset.Info.OrderNumber')
        : emptyString;
      let requestValue = chassisNumber ?? commandNumber ?? emptyString;
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(
          offerAssetApi.retrieveAssetData(chassisNumber, commandNumber),
          'AssetDataCommand',
          [eventTypes.ASSET_DATA_RETRIEVED],
          false
        )
      );
      if (!event.ok) throw new Error(event.eventBody.message);

      const assetData = event.eventBody.asset;

      let overriddenProps = {
        buildYearAndMonth: assetData.manufacturingDate || emptyString,
        km: assetData.currentMileage || 0,
        workingHours: assetData.workingHours || 0,
        acquisitionPriceWithoutVat: 0,
        acquisitionPriceWithVat: 0,
        hierarchy: getState().currentOffer.assetData.inEdit.asset.document.hierarchy.map(x =>
          x.level > 0 ? { ...x, id: null, name: null } : x
        ),
        itemId: null,
        properties: []
      };

      if (requestType === i18next.t('OfferEdit.Asset.Info.ChassisNumber')) {
        overriddenProps.orderNumber = assetData.commandNumber || emptyString;
      }
      if (requestType === i18next.t('OfferEdit.Asset.Info.OrderNumber')) {
        const chassisNumberFromData = assetData.chassisNumber;
        if (chassisNumberFromData) {
          overriddenProps.isChassisNumberAvailable = true;
          overriddenProps.chassisNumber = chassisNumberFromData;
        } else {
          overriddenProps.isChassisNumberAvailable = false;
        }
      }

      if (!assetData.empty) {
        dispatch(showInfo(i18next.t('Info.Asset.AssetDataLoaded', { requestType, requestValue })));
        const assetType = assetData.hierarchy.find(x => x.level === 0);

        overriddenProps.assetTypeId = assetType.id;
        overriddenProps.assetTypeName = assetType.name;
        overriddenProps.itemId = assetData.item?.itemId;
        overriddenProps.hierarchy = assetData.hierarchy;

        const lastLevel = assetData.hierarchy.find(x => x.triggers.properties && x.id);
        await dispatch(loadAssetProperties(assetData.item.itemId));
        let defaultProperties = getState().assetData.properties.byTypeId[assetData.item.itemId]?.properties;

        await dispatch(hierarchyActions.loadAssetHierarchyProperties(lastLevel.id));
        const assetHierarchyProperties = getState().assetData.assetHierarchyProperties[lastLevel.id];

        defaultProperties = replacePropertyValues(defaultProperties, assetData.properties);
        overriddenProps.properties = addOrUpdateAssetProperties(assetHierarchyProperties, defaultProperties, true);
        if (assetData.item?.itemId) {
          const { category, classification } = getState().currentOffer.assetData.inEdit.asset.document;
          const mandatoryPropertiesConfigured = await dispatch(
            getMandatoryPropertiesConfigured(assetData.item.itemId, assetType.id, category, classification, false)
          );
          if (mandatoryPropertiesConfigured.length > 0)
            overriddenProps.properties = overriddenProps.properties.map(property => {
              return {
                ...property,
                mandatory: mandatoryPropertiesConfigured.includes(property.propertyCode) ? true : property.mandatory
              };
            });
        }
      } else {
        dispatch(showWarning(i18next.t('Warnings.Asset.AssetDataNotFound', { requestType, requestValue })));
      }
      if (assetData.isSimilar) {
        dispatch(showWarning(i18next.t('Warnings.Asset.SimilarAssetLoaded')));
      }

      let asset = {
        ...getState().currentOffer.assetData.inEdit.asset.document,
        ...overriddenProps
      };
      const assetInEditPromise = dispatch({ type: actionTypes.SET_ASSET_IN_EDIT, payload: { asset } });
      const assetHierarchyTriggersPromise = dispatch(hierarchyActions.checkAssetHierarchyTriggers(asset.hierarchy));

      Promise.all([assetInEditPromise, assetHierarchyTriggersPromise]).then(() => {
        dispatch({ type: actionTypes.LOAD_ASSET_DATA_FINISHED });
        dispatch(setAssetDirty());
      });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_ASSET_DATA_FINISHED });
    }
  };
}

export function loadSecondHandProperties(itemId) {
  return async (dispatch, getState) => {
    if (itemId === undefined || itemId === null) return;
    if (getState().currentOffer.assetData.inEdit.secondHandProperties[itemId]) return;
    const result = await dispatch(sendHttpRequest(offerAssetApi.getSecondHandProperties(itemId)));
    dispatch({
      type: actionTypes.LOAD_SECOND_HAND_PROPERTIES_VISIBILITY_SUCCEEDED,
      payload: { itemId, hasWorkingHours: result.hasWorkingHours, defaultValue: result.defaultValue }
    });
  };
}

function setDefaultAssetProperties(assetTypeId, editRight, isFacilityFrame) {
  return async (dispatch, getState) => {
    if (isFacilityFrame) return;
    let properties = [];
    if (assetTypeId != null) {
      await dispatch(loadAssetProperties(assetTypeId));
      properties = getState().assetData.properties.byTypeId[assetTypeId].properties;
    }
    await dispatch(changeAssetProperty('properties', properties, editRight));
  };
}

const addOrUpdateAssetProperties = (assetHierarchyProperties, defaultProperties, skipNullableValue = false) => {
  let properties = [];

  properties = defaultProperties.map(defaultProperty => {
    const property = assetHierarchyProperties.find(x => x.propertyId === defaultProperty.propertyId);

    if (skipNullableValue && property && property.value) {
      return {
        ...defaultProperty,
        value: property.value
      };
    }

    if (!skipNullableValue && property) {
      return {
        ...defaultProperty,
        value: property.value
      };
    }

    return defaultProperty;
  });

  const assetHierarchyPropertiesToAdd = assetHierarchyProperties.filter(x => !defaultProperties.some(y => y.propertyId === x.propertyId));
  properties.push(...assetHierarchyPropertiesToAdd);

  return properties;
};

export function loadPropertyDetails(propId) {
  return async (dispatch, getState) => {
    const propertyDetailsLoaded = getState().assetData?.propertyDetails?.[propId]?.loaded;
    if (propertyDetailsLoaded) return;
    try {
      dispatch({ type: actionTypes.LOAD_PROPERTY_DETAILS_STARTED, id: propId });
      const propertyDetails = await dispatch(sendHttpRequest(offerAssetApi.getAssetPropertyDetails(propId)));
      dispatch({ type: actionTypes.LOAD_PROPERTY_DETAILS_SUCCESS, payload: { propertyDetails }, id: propId });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadPropByCode(propCode) {
  return offerAssetApi.getPropertyByCode(propCode);
}

export function loadPropertiesHistory(assetId) {
  return () => offerAssetApi.getPropertiesHistory(assetId);
}

export function chassisNumberSave(documentId, siteId, documentDetailId, chassisNumber, editRight, popupChassisNumberValidation = false) {
  return async dispatch => {
    dispatch({ type: actionTypes.UPDATE_OFFER_ASSET_CHASSIS_NO_STARTED });
    const event = await dispatch(
      sendHttpCommandAndWaitForEvent(
        offerAssetApi.updateOfferAssetChassisNumber(documentId, siteId, documentDetailId, chassisNumber),
        'updateChassisNumberCommand',
        [eventTypes.OFFER_ASSET_CHASSIS_NO_UPDATED]
      )
    );
    if (event.ok) {
      if (chassisNumber) dispatch(changeAssetProperty('isChassisNumberAvailable', true, editRight));
      else dispatch(changeAssetProperty('isChassisNumberAvailable', false, editRight));
      dispatch(changeAssetProperty('chassisNumber', chassisNumber, editRight, popupChassisNumberValidation));
      dispatch({
        type: actionTypes.UPDATE_OFFER_ASSET_CHASSIS_NUMBER_SUCCESS,
        payload: { chassisNumber, documentDetailId }
      });
    }
  };
}

export function splitAsset(documentId, siteId, documentDetailId, quantity, editRight) {
  return async dispatch => {
    dispatch({ type: actionTypes.SPLIT_OFFER_ASSET_STARTED });
    const event = await dispatch(
      sendHttpCommandAndWaitForEvent(offerAssetApi.splitAsset(documentId, siteId, documentDetailId, quantity), 'assetSplitedCommand', [
        eventTypes.ASSET_SPLIT
      ])
    );
    if (event.ok) {
      await dispatch(changeAssetProperty('withIndividualAssetData', true, editRight));
      await dispatch(changeAssetProperty('assetInformations', event.eventBody.assetInformations, editRight));
      await dispatch(changeAssetProperty('quantity', event.eventBody.quantity, editRight));

      dispatch({ type: actionTypes.RESET_DIRTY_INFO });
      dispatch({ type: actionTypes.SPLIT_OFFER_ASSET_SUCCEEDED });
    }
  };
}

export function saveAssetInformation(documentId, siteId, documentDetailId, assetInformation, editRight) {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.SAVE_OFFER_ASSET_INFORMATION_STARTED });
    const event = await dispatch(
      sendHttpCommandAndWaitForEvent(
        offerAssetApi.saveAssetInformation(documentId, siteId, documentDetailId, assetInformation),
        'assetInformationSavedCommand',
        [eventTypes.ASSET_INFORMATION_SAVED]
      )
    );
    if (event.ok) {
      const asset = getState().currentOffer.assetData.inEdit.asset.document;
      const { assetInformations } = asset;
      const idx = assetInformations.findIndex(x => x.orderNo === assetInformation.orderNo);
      let newAssetInformations = Object.assign([], { ...assetInformations });
      if (idx === -1) {
        newAssetInformations.push(event.eventBody.assetInformation);
        await dispatch(changeAssetProperty('quantity', asset.quantity + 1, editRight));
      } else {
        newAssetInformations = Object.assign([], {
          ...newAssetInformations,
          [idx]: {
            ...event.eventBody.assetInformation
          }
        });
      }

      await dispatch(changeAssetProperty('assetInformations', newAssetInformations, editRight));

      dispatch({ type: actionTypes.RESET_DIRTY_INFO });
      dispatch({ type: actionTypes.SAVE_OFFER_ASSET_INFORMATION_SUCCEEDED });
    }
  };
}

export function deleteAssetInformation(documentId, siteId, documentDetailId, assetInformation, editRight) {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.DELETE_OFFER_ASSET_INFORMATION_STARTED });
    const event = await dispatch(
      sendHttpCommandAndWaitForEvent(
        offerAssetApi.deleteAssetInformation(documentId, siteId, documentDetailId, assetInformation),
        'deleteAssetInformationCommand',
        [eventTypes.ASSET_INFORMATION_DELETED]
      )
    );
    if (event.ok) {
      const asset = getState().currentOffer.assetData.inEdit.asset.document;
      const { assetInformations } = asset;
      const idx = assetInformations.findIndex(x => x.id === assetInformation.id);
      const newAssetInformations = collection.remove(idx, assetInformations);
      await dispatch(changeAssetProperty('assetInformations', newAssetInformations, editRight));
      await dispatch(changeAssetProperty('quantity', event.eventBody.quantity, editRight));

      dispatch({ type: actionTypes.RESET_DIRTY_INFO });
      dispatch({ type: actionTypes.DELETE_OFFER_ASSET_INFORMATION_SUCCEEDED });
    }
  };
}

export function cancelChassisNumberSave(initialChassisNumber) {
  return async dispatch => {
    dispatch({ type: actionTypes.UPDATE_OFFER_ASSET_CHASSIS_CANCELED, payload: initialChassisNumber });
  };
}

export function loadContactInfo(search, role) {
  //no redux cache
  return async (dispatch, getState) => {
    try {
      const partner = getState().currentOffer.assetData.inEdit.asset.document?.partner;
      if (!partner) return;
      const partnerId = role === 'dealer' ? partner.dealer.dealerId : partner.supplier.supplierId;
      return offerAssetApi.getContactInfo(search, partnerId, role);
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

function getMandatoryPropertiesConfigured(assetId, assetTypeId, category, classification, isFacilityFrame) {
  return async (dispatch, getState) => {
    if (!assetId || isFacilityFrame) return [];
    const { isAssetPropertyBehaviorConfigs } = getState().offerConfig;
    const key = `${assetId}_${assetTypeId}_${category ?? ''}_${classification ?? ''}`;
    let mandatoryPropertiesConfigured = getState().assetData.mandatoryPropertiesConfigured[key];
    if (!mandatoryPropertiesConfigured?.loaded && isAssetPropertyBehaviorConfigs) {
      await dispatch(loadMandatoryPropertiesConfigured(assetId, assetTypeId, category, classification));
      mandatoryPropertiesConfigured = getState().assetData.mandatoryPropertiesConfigured[key];
    }

    return mandatoryPropertiesConfigured?.properties || [];
  };
}

function changeAssetItem(property, popupChassisNumberValidation, editRight, isFacilityFrame) {
  return async (dispatch, getState) => {
    const { vatPercent } = getState().offerConfig;
    const asset = getState().currentOffer.assetData.inEdit.asset.document;
    const { viewType } = getState().params.items;

    if (property.itemId !== asset.itemId) {
      await dispatch(
        getMandatoryPropertiesConfigured(property.itemId, asset.assetTypeId, asset.category, asset.classification, isFacilityFrame)
      );
      dispatch({
        type: actionTypes.CHANGE_ASSET_PROPERTY,
        payload: {
          prop: 'itemId',
          value: property.itemId,
          vatPercent,
          editRight,
          popupChassisNumberValidation,
          isSecondHand: asset.isSecondHand,
          isFacilityFrame,
          viewType
        }
      });

      await dispatch(setDefaultAssetProperties(property.itemId, editRight, isFacilityFrame));
    }

    const hierarchy = getState().currentOffer.assetData.inEdit.asset.document.hierarchy;
    const hierarchyLevel = hierarchy.find(x => x.triggers.properties && x.id);
    if (hierarchy && hierarchyLevel) {
      if (!isFacilityFrame) await dispatch(hierarchyActions.loadAssetHierarchyProperties(hierarchyLevel.id));
      const assetHierarchyProperties = getState().assetData.assetHierarchyProperties[hierarchyLevel.id];
      let properties = getState().currentOffer.assetData.inEdit.asset.document.properties;
      if (!isFacilityFrame) properties = addOrUpdateAssetProperties(assetHierarchyProperties, properties);

      const { isSecondHand } = getState().currentOffer.assetData.inEdit.asset.document;
      await dispatch(changeAssetProperty('properties', properties, editRight, popupChassisNumberValidation, isSecondHand, isFacilityFrame));
      const mandatoryPropertiesConfigured = await dispatch(
        getMandatoryPropertiesConfigured(property.itemId, asset.assetTypeId, asset.category, asset.classification, isFacilityFrame)
      );
      dispatch({
        type: actionTypes.VALIDATE_ASSET,
        payload: { financedAssetDealerWrite: editRight, isSecondHand, isFacilityFrame, mandatoryPropertiesConfigured, viewType }
      });
    }
  };
}

export function changeAssetProperty(
  prop,
  value,
  editRight,
  popupChassisNumberValidation = false,
  isFacilityFrame = false,
  isAlphaFunctionality = false,
  shouldSearchExternal = true
) {
  return async (dispatch, getState) => {
    const { vatPercent } = getState().offerConfig;
    let { isSecondHand } = getState().currentOffer.assetData.inEdit.asset.document;
    const { viewType } = getState().params.items;
    const assetTypeCodes = getState().assetData?.assetTypes;

    const assetTypesContextFormula = getAssetTypesContextFormula(dispatch, getState)(prop);

    if (prop === 'category') isSecondHand = value === 'Used' || value === 'Ex-Demo';

    if (prop !== 'shouldRetrieveSupplierInfo') {
      const asset = getState().currentOffer.assetData.inEdit.asset.document;
      if (prop === 'category')
        await dispatch(getMandatoryPropertiesConfigured(asset.itemId, asset.assetTypeId, value, asset.classification, isFacilityFrame));
      else if (prop === 'classification')
        await dispatch(getMandatoryPropertiesConfigured(asset.itemId, asset.assetTypeId, asset.category, value, isFacilityFrame));

      dispatch({
        type: actionTypes.CHANGE_ASSET_PROPERTY,
        payload: {
          prop,
          value,
          vatPercent,
          financedAssetDealerWrite: editRight,
          popupChassisNumberValidation,
          isSecondHand,
          isFacilityFrame,
          viewType,
          assetTypesContextFormula,
          assetTypeCodes
        }
      });
    }
    const asset = getState().currentOffer.assetData.inEdit.asset.document;

    const partnerExternalSearch = isAlphaFunctionality ? externalSearchForPartnerAlpha : externalSearchForPartner;
    switch (prop) {
      case 'partner.dealer.dealerId': {
        if (!!value && isAlphaFunctionality) {
          await dispatch(getSupplierInfo(value)).then(async data => {
            dispatch({
              type: actionTypes.CHANGE_ASSET_PROPERTY,
              payload: {
                prop: 'partner.supplierType',
                value: data?.supplierType,
                vatPercent,
                financedAssetDealerWrite: editRight,
                popupChassisNumberValidation,
                isSecondHand,
                isFacilityFrame,
                viewType
              }
            });

            if (!!data && !data.supplierType === supplierType.Other) {
              await dispatch(
                changeAssetProperty(
                  'partner.supplier.registrationCode',
                  data.supplierUniqueId,
                  editRight,
                  popupChassisNumberValidation,
                  isFacilityFrame,
                  isAlphaFunctionality,
                  true
                )
              );
            }
          });
        }
        break;
      }

      case 'properties': {
        const behaviorParamJson = getState().params?.items?.assetTypes;
        const assetTypeCodes = getState().assetData.assetTypes;
        const assetTypeCode = assetTypeCodes?.find(item => item.assetTypeId === asset.assetTypeId)?.code;
        if (behaviorParamJson) {
          try {
            const behaviorParam = camelizeKeys(JSON?.parse(behaviorParamJson));
            var acquisitionPriceWithoutVat = getState().currentOffer.assetData.inEdit.asset.document.acquisitionPriceWithoutVat;
            behaviorParam.forEach(x => {
              var properties = getState().currentOffer.assetData.inEdit.asset.document.properties;
              var propertyIndex = properties.findIndex(y => y.propertyId === x.propertyId);
              x.rules.forEach(z => {
                if (
                  acquisitionPriceWithoutVat > z.assetMinValue &&
                  acquisitionPriceWithoutVat <= z.assetMaxValue &&
                  z.assetTypes.includes(assetTypeCode)
                ) {
                  dispatch(
                    changeAssetProperty(
                      'properties.' + propertyIndex + '.value',
                      z.value,
                      editRight,
                      popupChassisNumberValidation,
                      isFacilityFrame,
                      isAlphaFunctionality
                    )
                  );
                }
              });
            });
          } catch (error) {
            dispatch(showError(error.message, error.correlationId));
            throw error;
          }
        }
        break;
      }

      case 'partner.dealer.registrationCode': {
        if (!!shouldSearchExternal && !!value) {
          await dispatch(partnerExternalSearch({ registrationCode: value })).then(externalPartner => {
            dispatch(
              changeAssetProperty(
                'partner.dealer.name',
                externalPartner?.partnerName,
                editRight,
                popupChassisNumberValidation,
                isFacilityFrame,
                isAlphaFunctionality
              )
            );
            dispatch(
              changeAssetProperty(
                'partner.dealer.dealerId',
                externalPartner?.partnerId,
                editRight,
                popupChassisNumberValidation,
                isFacilityFrame,
                isAlphaFunctionality
              )
            );
          });
        }
        break;
      }

      case 'partner.supplier.registrationCode': {
        if (!!shouldSearchExternal && !!value) {
          await dispatch(partnerExternalSearch({ registrationCode: value })).then(externalPartner => {
            dispatch(
              changeAssetProperty(
                'partner.supplier.name',
                externalPartner?.partnerName,
                editRight,
                popupChassisNumberValidation,
                isFacilityFrame,
                isAlphaFunctionality
              )
            );
            dispatch(
              changeAssetProperty(
                'partner.supplier.supplierId',
                externalPartner?.partnerId,
                editRight,
                popupChassisNumberValidation,
                isFacilityFrame,
                isAlphaFunctionality
              )
            );
          });
        }
        break;
      }

      case 'partner.supplierType': {
        if (value === supplierType.Other && asset.partner?.dealer?.dealerId && !asset.partner?.supplier?.registrationCode) {
          await dispatch(getSupplierInfo(asset.partner?.dealer?.dealerId)).then(async data => {
            if (!!data && !data.supplierType === supplierType.Other) {
              await dispatch(
                changeAssetProperty(
                  'partner.supplier.registrationCode',
                  data.supplierUniqueId,
                  editRight,
                  popupChassisNumberValidation,
                  isFacilityFrame,
                  isAlphaFunctionality
                )
              );
            }
          });
        }
        break;
      }

      case 'shouldRetrieveSupplierInfo': {
        await dispatch(partnerExternalSearch({ registrationCode: value })).then(externalPartner => {
          dispatch(
            changeAssetProperty(
              'partner.supplier',
              {
                registrationCode: value,
                supplierId: externalPartner.partnerId,
                name: externalPartner.partnerName
              },
              editRight,
              popupChassisNumberValidation,
              isFacilityFrame,
              isAlphaFunctionality,
              false
            )
          );

          dispatch({ type: actionTypes.RESET_DIRTY_INFO });
        });
        break;
      }

      case 'itemId': {
        dispatch(setDefaultAssetProperties(value, editRight, isFacilityFrame));
        break;
      }

      case 'hierarchyLevel': {
        dispatch(changeAssetItem(value, popupChassisNumberValidation, editRight, isFacilityFrame));
        dispatch(hierarchyActions.checkAssetHierarchyTriggers(asset.hierarchy));
        break;
      }

      default:
        break;
    }

    if (['category', 'classification'].includes(prop)) {
      if (!asset.itemId) return;
      const mandatoryPropertiesConfigured = await dispatch(
        getMandatoryPropertiesConfigured(asset.itemId, asset.assetTypeId, asset.category, asset.classification, isFacilityFrame)
      );
      let initialProperties = getState().assetData.properties.byTypeId[asset.itemId]?.properties;
      if (!initialProperties && !isFacilityFrame) {
        await dispatch(loadAssetProperties(asset.itemId));
        initialProperties = getState().assetData.properties.byTypeId[asset.itemId]?.properties;
      }

      dispatch({
        type: actionTypes.VALIDATE_ASSET,
        payload: {
          financedAssetDealerWrite: editRight,
          isSecondHand,
          isFacilityFrame,
          initialProperties,
          mandatoryPropertiesConfigured,
          viewType
        }
      });
    }
  };
}

const getAssetTypesContextFormula = (dispatch, getState) => property => {
  const assetTypesParams = getState().params.items?.assetTypes;

  if (!assetTypesParams || !(property === 'acquisitionPriceWithoutVat' || property === 'acquisitionPriceWithVat')) {
    return null;
  }

  try {
    const json = JSON.parse(assetTypesParams);
    return camelizeKeys(json);
  } catch (error) {
    dispatch(showError(error.message, error.correlationId));
    throw error;
  }
};

function externalSearchForPartner(data) {
  return async dispatch => {
    const event = await dispatch(
      sendHttpCommandAndWaitForEvent(
        offerAssetApi.externalSearchForPartner(data),
        'ExternalSearchForPartnerCommand',
        [offerAssetEventTypes.FROM_PARTNER_QUERY_FOUND],
        false
      )
    );
    if (event.ok) {
      return event.eventBody;
    }
  };
}

function externalSearchForPartnerAlpha(data) {
  return async dispatch => {
    const event = await dispatch(
      sendHttpCommandAndWaitForEvent(
        offerAssetApi.externalSearchForPartnerAlpha(data),
        'ExternalSearchForPartnerCommand',
        [offerAssetEventTypes.FROM_PARTNER_QUERY_FOUND],
        false
      )
    );
    if (event.ok) {
      return event.eventBody;
    }
  };
}

function getSupplierInfo(dealerId) {
  if (!dealerId) return null;
  return dispatch =>
    offerAssetApi.getSupplierInfo(dealerId).catch(error => {
      dispatch(showError(error.message, error.correlationId));
    });
}

const mergeHierarchyLevels = (hierarchy, emptyAssetHierarchy) => {
  return emptyAssetHierarchy.map(z => {
    const hierarchyLevel = hierarchy.find(x => x.level === z.level);
    return hierarchyLevel || z;
  });
};

const assetIsSameTypeWithOthers = (getState, dispatch) => {
  const doc = getState().currentOffer;
  const params = getState().params;

  let hasErrors = false;

  const { documentTypeId } = doc.header;
  if (documentTypeId !== documentTypeIds.Offer) return true;

  const identicalAssetCond = getIdenticalAssetConditions(params);
  const propertyCodes = identicalAssetCond ? identicalAssetCond.PropertyCodes : [];
  const fieldNames = identicalAssetCond ? camelizeKeys(identicalAssetCond.FieldNames) : [];

  const asset = doc.assetData.inEdit.asset.document;
  const assetIsAdded = !Number.isInteger(asset.documentDetailId);

  if (fieldNames) {
    fieldNames.map(field => {
      if (field.frontName.toUpperCase().includes('ASSETHIERARCHYID')) {
        const currentAssetHierarchyId = getAssetHierarchyId(asset.hierarchy);
        const anyDifferent = doc.assetData.assets.find(z => getAssetHierarchyId(z.hierarchy) !== currentAssetHierarchyId);
        if (currentAssetHierarchyId && anyDifferent && (assetIsAdded || doc.assetData.assets.length > 1)) hasErrors = true;
      } else {
        const currentFieldValue = asset[camelize(field.frontName)];
        const anyDifferent = doc.assetData.assets.find(z => z[camelize(field.frontName)] !== currentFieldValue);
        if (currentFieldValue && anyDifferent && (assetIsAdded || doc.assetData.assets.length > 1)) hasErrors = true;
      }
      return false;
    });
  }

  if (propertyCodes) {
    propertyCodes.map(propertyCode => {
      const currentFieldValue = asset.properties.find(z => z.propertyCode === propertyCode)?.value;
      const anyDifferent = doc.assetData.assets.find(
        z => z.properties.find(z => z.propertyCode === propertyCode)?.value !== currentFieldValue
      );

      if (currentFieldValue && anyDifferent && (assetIsAdded || doc.assetData.assets.length > 1)) hasErrors = true;

      if (hasErrors) {
        dispatch(showError(i18next.t('Errors.Asset.InappropriateAsset')));
        return false;
      }

      return false;
    });
  }

  return true;
};

const validateAsset = (asset, isFacilityFrame, getState, dispatch) => {
  const hierarchy = applyHierarchyValidationRules(asset.document.hierarchy, isFacilityFrame);
  const validHierarchy = hierarchyIsValid(hierarchy);
  const sameType = assetIsSameTypeWithOthers(getState, dispatch);
  const valid = asset.validationInfo.isValid && (isFacilityFrame || asset.document.itemId) && validHierarchy && sameType;

  if (!isFacilityFrame) {
    const error = asset.document.itemId ? null : i18next.t('Errors.Asset.NoAssetSelected');
    if (error) dispatch(showError(error));
  }

  return valid;
};

const getRequestType = (isFacilityFrame, getState) => {
  if (isFacilityFrame === true) {
    const localDictionaries = getState().dictionaries;
    const implicitItem = localDictionaries.offerTypes[documentTypeIds.FacilityFrameOffer].offerTypes.find(item => item.implicit === true);

    return implicitItem?.offerTypeId || offerTypes.facilityOffer;
  }

  return null;
};

export function saveAsset(callbackFn = () => {}, isFacilityFrame, editRight) {
  return async (dispatch, getState) => {
    const { viewType } = getState().params.items;
    const { isSecondHand, itemId, assetTypeId, category, classification } = getState().currentOffer.assetData.inEdit.asset.document;

    const key = `${itemId ?? ''}_${assetTypeId ?? ''}_${category ?? ''}_${classification ?? ''}`;
    const mandatoryPropertiesConfigured = getState().assetData.mandatoryPropertiesConfigured[key]?.properties || [];

    dispatch({
      type: actionTypes.VALIDATE_ASSET,
      payload: { financedAssetDealerWrite: editRight, isSecondHand, isFacilityFrame, mandatoryPropertiesConfigured, viewType }
    });

    let asset = cloneDeep(getState().currentOffer.assetData.inEdit.asset);
    if (!validateAsset(asset, isFacilityFrame, getState, dispatch)) return;

    if (asset.document.buildYearAndMonth)
      asset.document.buildYearAndMonth = format(new Date(asset.document.buildYearAndMonth), 'yyyy-MM-dd').toString();

    asset.document.properties.forEach(prop => {
      if (getDynamicFieldControlType(prop.typeCode) !== ControlType.Date || !prop.value) return;
      prop.value = format(new Date(prop.value), 'yyyy-MM-dd').toString();
    });

    const requestTypeId = getRequestType(isFacilityFrame, getState);

    dispatch({ type: actionTypes.SAVE_OFFER_ASSET_STARTED });
    const hasWorkingHours = getState().currentOffer.assetData.inEdit.secondHandProperties[asset.document.itemId]?.hasWorkingHours;
    const defaultValue = getState().currentOffer.assetData.inEdit.secondHandProperties[asset.document.itemId]?.defaultValue;
    const newAsset = createRequestBody({
      asset: asset.document,
      isFacilityFrame,
      requestTypeId,
      hasWorkingHours,
      defaultValue
    });
    let event = null;
    try {
      if (Number.isInteger(newAsset.documentId) && Number.isInteger(newAsset.documentDetailId)) {
        event = await dispatch(updateOfferAsset(newAsset));
      } else if (Number.isInteger(newAsset.documentId) && !Number.isInteger(newAsset.documentDetailId)) {
        event = await dispatch(createOfferAsset(newAsset));
      } else {
        event = await dispatch(createOffer(newAsset));
      }

      if (event.ok) {
        const { documentId, siteId, documentDetailId } = event.eventBody;
        await dispatch({
          type: actionTypes.SAVE_OFFER_ASSET_SUCCEEDED,
          payload: { documentDetailId: newAsset.documentDetailId }
        });
        if (!Number.isInteger(newAsset.documentId) || !Number.isInteger(newAsset.documentDetailId))
          callbackFn(documentId, siteId, documentDetailId);
      } else {
        dispatch({ type: actionTypes.SAVE_OFFER_ASSET_FAILED });
      }
    } catch (error) {
      dispatch({ type: actionTypes.SAVE_OFFER_ASSET_FAILED });
      dispatch(showError(error.message));
    }
  };
}

function createOfferAsset(asset) {
  return async dispatch => {
    const event = await dispatch(
      sendHttpCommandAndWaitForEvent(offerAssetApi.createOfferAsset(asset), 'RunningOfferCommand', [eventTypes.OFFER_ASSET_CREATED])
    );
    return event;
  };
}

function updateOfferAsset(asset) {
  return async dispatch => {
    const event = await dispatch(
      sendHttpCommandAndWaitForEvent(offerAssetApi.updateOfferAsset(asset), 'RunningOfferCommand', [eventTypes.OFFER_ASSET_UPDATED])
    );
    return event;
  };
}

export function cancelAssetChanges(documentId, siteId) {
  return { type: actionTypes.CANCEL_OFFER_ASSET_CHANGES, payload: { documentId, siteId } };
}

const createRequestBody = args => {
  const { asset, isFacilityFrame, requestTypeId, hasWorkingHours, defaultValue } = args;
  const {
    documentId,
    documentDetailId,
    siteId,
    assetTypeId,
    itemId,
    currencyId,
    partner,
    quantity,
    acquisitionPriceWithoutVat,
    acquisitionPriceWithVat,
    isSecondHand,
    buildYearAndMonth,
    km,
    workingHours,
    properties,
    isChassisNumberAvailable,
    remat,
    firstOwner,
    chassisNumber,
    equipments,
    orderNumber,
    category,
    classification,
    userPartnerId,
    workLocationId
  } = asset;

  return {
    documentId,
    documentDetailId,
    siteId,
    assetTypeId,
    itemId,
    currencyId,
    dealerId: partner.dealer ? partner.dealer.dealerId : null,
    dealerRegistrationCode: partner.dealer ? partner.dealer.registrationCode : null,
    dealerContactInfo: partner.dealer ? partner.dealer.contactInfo : null,
    supplierType: partner.supplierType,
    supplierId: partner.supplier ? partner.supplier.supplierId : null,
    supplierRegistrationCode: partner.supplier ? partner.supplier.registrationCode : null,
    supplierContactInfo: partner.supplier ? partner.supplier.contactInfo : null,
    quantity,
    acquisitionPriceWithoutVat,
    acquisitionPriceWithVat,
    reevaluationPriceWithoutVat: asset.reevaluationPriceWithoutVat ? asset.reevaluationPriceWithoutVat : null,
    isSecondHand,
    buildYearAndMonth,
    km,
    workingHours,
    properties: properties || [],
    isChassisNumberAvailable,
    remat,
    firstOwner,
    chassisNumber,
    equipments,
    isFacilityFrame,
    assetHierarchyId: getAssetHierarchyId(asset.hierarchy) || asset.assetTypeId,
    hierarchy: asset.hierarchy.filter(x => x.id !== null),
    requestTypeId,
    orderNumber,
    category,
    classification,
    hasWorkingHours,
    defaultValue,
    userPartnerId: userPartnerId ?? null,
    workLocationId: workLocationId ?? null
  };
};
